/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/browser'
import React, { useEffect, useState, FormEvent } from 'react'
import { isEmpty } from 'lodash'
import { flowRight as compose } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { generatePath, RouteChildrenProps } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { loaderState } from 'recoils/atoms'
import { useSetRecoilState } from 'recoil'

import { Button, PromptGuard, Modal } from 'components'
import { Confirmation, ModalSuccess } from 'components/Modal'
import {
  StockKeepingUnitForm,
  initialStockKeepingUnitForm,
  schemaStockKeepingUnitForm,
  formatForm,
  listUploadImg,
  renderBreadcrumbsEdit,
} from 'features/StockKeepingUnit'
import {
  StockKeepingUnitService,
  generateSelectedCategoriesOption,
} from 'features/StockKeepingUnit'
import { withBackOfficeCms } from 'helpers/hocs'
import { routes } from 'helpers/routes'
import { findSelected, setPriceDigitsFormatter } from 'helpers/common'
import { MAINTENANCE_UNIT_OPTIONS } from 'helpers/constants'

import {
  useBreadcrumbs,
  useFormSubmitLoader,
  usePromptForm,
} from 'helpers/hooks'

import type {
  IStockKeepingUnitForm,
  IStockKeepingUnitFormPayload,
  IFileList,
} from 'features/StockKeepingUnit'

const StockKeepingUnitEditContainer = ({
  history,
  match,
}: RouteChildrenProps<{ id: string }>): JSX.Element => {
  const [payload, setPayload] = useState<IStockKeepingUnitFormPayload>()
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false)
  const [isOpenSuccess, setIsOpenSuccess] = useState<boolean>(false)
  const [fileList, setFileList] = useState<IFileList[]>([])

  const { t } = useTranslation(['common', 'layout'])

  const id = match?.params.id || ''
  const base = 'con-stock-keeping-unit__form'

  const [{ data: sku, loading: skuLoading, error }] =
    StockKeepingUnitService.useFindOneById(id)
  const setLoader = useSetRecoilState(loaderState)
  const methods = useForm<IStockKeepingUnitForm>({
    defaultValues: initialStockKeepingUnitForm,
    resolver: yupResolver(schemaStockKeepingUnitForm),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })
  const [shouldPrompt, setShouldPrompt] =
    usePromptForm<IStockKeepingUnitForm>(methods)
  const [loading] = useFormSubmitLoader<IStockKeepingUnitForm>(methods)

  useBreadcrumbs(renderBreadcrumbsEdit(id))

  const callApi = async () => {
    if (payload) {
      try {
        setLoader(true)

        const photoFile: listUploadImg[] = []
        const photoPath: IFileList[] = []
        fileList.forEach((e, index) => {
          if (e.file) {
            photoFile.push({ file: e.file, index })
          } else {
            photoPath.push({ path: e.path, index })
          }
        })
        if (photoFile.length > 0) {
          await StockKeepingUnitService.uploadImg(id, {
            image: photoFile,
          }).then(async (res: any) => {
            const list = [
              ...photoPath,
              ...res.body.map((e: any, i: number) => {
                return { path: e.name, index: photoFile[i].index }
              }),
            ].sort((a: any, b: any) => a.index - b.index)
            payload.photos = list.map((e) => {
              return e.path
            })
          })
        } else {
          payload.photos =
            photoPath.length === 0
              ? null
              : photoPath.map((e) => {
                  return e.path
                })
        }
        await StockKeepingUnitService.update(id, payload, handleOpenSuccess)
        setLoader(false)
      } catch (error) {
        Sentry.captureException(error)
      }
    }
  }
  const handleOpenSuccess = async () => {
    setIsOpenSuccess(true)

    await setTimeout(() => {
      setShouldPrompt(false)
      history.push(routes.product.path)
    }, 2000)
  }

  const setData = (data: IStockKeepingUnitForm) => {
    const payload = formatForm(data, true)
    if (isEmpty(methods.errors) && payload) {
      setIsOpenConfirm(true)
      setPayload(payload)
    }
  }

  const beforeSubmit = (e: FormEvent<HTMLFormElement>) => {
    methods.setValue('currentState', 'onSubmit')
    methods.setValue('originalSkuCode', sku?.code)
    methods.handleSubmit(setData)(e)
    methods.setValue('currentState', '')
    methods.setValue('originalSkuCode', '')
  }

  useEffect(() => {
    const img: IFileList[] = []
    if (sku?.photos) {
      sku.photos.forEach((e) => {
        img.push({
          path: e,
        })
      })
    }
    setFileList(img)
  }, [sku])

  useEffect(() => {
    setLoader(skuLoading)
  }, [skuLoading])

  useEffect(() => {
    if (!sku) {
      return
    }

    if (error) {
      Sentry.captureException(error)

      return
    }

    const getCostCurrency = sku.costCurrency ? `${sku.costCurrency}` : 'THB'
    const getSaleCurrency = sku.saleCurrency ? `${sku.saleCurrency}` : 'THB'
    const costPrice = setPriceDigitsFormatter(sku.costPrice, getCostCurrency)
    const salePrice = setPriceDigitsFormatter(sku.salePrice, getSaleCurrency)

    methods.reset({
      skuCode: sku.code,
      name: sku.name,
      model: `${sku.model}`,
      brand: `${sku.brand}`,
      categories: generateSelectedCategoriesOption(sku.categories),
      costPrice,
      costCurrency: {
        key: getCostCurrency,
        label: getCostCurrency,
      },
      salePrice,
      saleCurrency: {
        key: getSaleCurrency,
        label: getSaleCurrency,
      },
      maintenancePeriod: sku.maintenancePeriod
        ? `${sku.maintenancePeriod}`
        : '',
      maintenanceUnit: {
        key: `${sku.maintenanceUnit}`,
        label: findSelected(MAINTENANCE_UNIT_OPTIONS, `${sku.maintenanceUnit}`)
          ?.label,
      },
      detail: sku.detail ? `${sku.detail}` : '',
      isActive: sku.isActive,
    })
  }, [sku])

  useEffect(() => {
    if (isOpenSuccess) {
      handleOpenSuccess()
    }
  }, [isOpenSuccess])

  const renderConfirmModal = () => {
    return (
      isOpenConfirm && (
        <Modal
          isIcon={false}
          isOpen={isOpenConfirm}
          onClose={() => setIsOpenConfirm(false)}
        >
          <Confirmation
            title={t('common:confirmation.title.label')}
            description={t('common:confirmation.description.label')}
            confirmText={t('common:save.button')}
            cancelText={t('common:cancel.button')}
            onConfirm={callApi}
            onClose={() => setIsOpenConfirm(false)}
          />
        </Modal>
      )
    )
  }

  const renderSuccessModal = () => {
    return (
      isOpenSuccess && (
        <ModalSuccess
          isOpen={isOpenSuccess}
          description={t('common:success.label')}
          onClose={() => setIsOpenSuccess(false)}
        />
      )
    )
  }

  return (
    <FormProvider {...methods}>
      <PromptGuard when={shouldPrompt} />
      <form className={base} onSubmit={beforeSubmit}>
        <StockKeepingUnitForm
          isEdit
          previousCode={sku?.code}
          setFileList={setFileList}
          fileList={fileList}
        />
        <div className='container__form__actions'>
          <Button
            variants='secondary'
            onClick={() =>
              history.push(generatePath(routes.product.path, { id }))
            }
          >
            {t('common:cancel.button')}
          </Button>
          <Button type='submit' isLoading={loading}>
            {t('common:save.button')}
          </Button>
        </div>
      </form>
      {renderConfirmModal()}
      {renderSuccessModal()}
    </FormProvider>
  )
}

export default compose(withBackOfficeCms)(StockKeepingUnitEditContainer)
